<template>
  <!-- One row: a filter cluster (dates + quick ranges) on the left, actions on the right.
       Filters share one bordered container; exports are outlined so the two groups read
       as different kinds of control. -->
  <div class="flex flex-col xl:flex-row xl:items-center gap-3">
    <div
      class="flex flex-wrap items-center gap-1.5 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-1.5"
      role="group"
      aria-label="Reporting period"
    >
      <div class="flex items-center gap-1 px-1">
        <input
          type="date"
          :value="startDate"
          :max="maxDate"
          aria-label="Start date"
          class="h-8 px-2 rounded-lg text-sm bg-transparent text-neutral-900 dark:text-neutral-100 border border-transparent hover:border-neutral-300 dark:hover:border-neutral-600 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
          @change="$emit('update:startDate', $event.target.value)"
        />
        <span class="text-xs text-neutral-400 dark:text-neutral-500 select-none">to</span>
        <input
          type="date"
          :value="endDate"
          :max="maxDate"
          aria-label="End date"
          class="h-8 px-2 rounded-lg text-sm bg-transparent text-neutral-900 dark:text-neutral-100 border border-transparent hover:border-neutral-300 dark:hover:border-neutral-600 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
          @change="$emit('update:endDate', $event.target.value)"
        />
      </div>

      <span class="hidden sm:block w-px h-6 bg-neutral-200 dark:bg-neutral-700 mx-0.5" aria-hidden="true"></span>

      <div class="flex flex-wrap items-center gap-1" role="group" aria-label="Quick date ranges">
        <button
          v-for="toggle in quickToggles"
          :key="toggle.label"
          type="button"
          :aria-pressed="(activeToggle === toggle.label).toString()"
          :class="[
            'h-8 px-3 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            activeToggle === toggle.label
              ? 'bg-primary-600 text-white shadow-sm'
              : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700',
          ]"
          @click="$emit('apply-range', toggle)"
        >
          {{ toggle.label }}
        </button>
      </div>
    </div>

    <div v-if="exports" class="flex items-center gap-2 xl:ml-auto" role="group" aria-label="Export">
      <button
        v-for="fmt in formats"
        :key="fmt.key"
        type="button"
        :disabled="!!downloading"
        :aria-busy="(downloading === fmt.key).toString()"
        class="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-semibold border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        @click="$emit('download', fmt.key)"
      >
        <svg v-if="downloading === fmt.key" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span v-else class="material-icons text-[16px]" aria-hidden="true">{{ fmt.icon }}</span>
        {{ fmt.label }}
      </button>
    </div>
  </div>
</template>

<script>
// Shared header controls for every analytics view. State lives in the view's
// useDateRange(); this component only renders it and emits intent.
const FORMATS = [
  { key: 'pdf', label: 'PDF', icon: 'picture_as_pdf' },
  { key: 'csv', label: 'CSV', icon: 'table_chart' },
]

export default {
  name: 'AnalyticsToolbar',
  props: {
    startDate: { type: String, required: true },
    endDate: { type: String, required: true },
    maxDate: { type: String, default: '' },
    activeToggle: { type: String, default: null },
    quickToggles: { type: Array, default: () => [] },
    downloading: { type: String, default: null }, // 'pdf' | 'csv' while a report generates
    exports: { type: Boolean, default: true },
  },
  emits: ['update:startDate', 'update:endDate', 'apply-range', 'download'],
  setup() {
    return { formats: FORMATS }
  },
}
</script>
